import * as React from 'react'
import { cn } from '../../utils/cn'

/**
 * JPG Academy Table
 *
 * Compound component for displaying tabular data with consistent styling.
 * Matches Input/Button form language: cream-tinted headers, warm borders,
 * gold focus rings on interactive rows.
 */

// ─── Table Root ─────────────────────────────────────────────────────────────

export interface TableProps extends React.TableHTMLAttributes<HTMLTableElement> {
	/** Show alternating row backgrounds */
	striped?: boolean
	/** Compact mode — tighter padding */
	compact?: boolean
}

const TableContext = React.createContext<{ striped: boolean; compact: boolean }>({
	striped: false,
	compact: false,
})

export function Table({ striped = false, compact = false, className, ...props }: TableProps) {
	return (
		<TableContext.Provider value={{ striped, compact }}>
			<div className="relative w-full overflow-auto scrollbar-warm">
				<table
					className={cn('w-full caption-bottom text-sm font-ui', compact ? 'text-xs' : 'text-sm', className)}
					{...props}
				/>
			</div>
		</TableContext.Provider>
	)
}

// ─── Table Header ───────────────────────────────────────────────────────────

export interface TableHeaderProps extends React.HTMLAttributes<HTMLTableSectionElement> {
	/** Make header sticky */
	sticky?: boolean
}

export function TableHeader({ sticky = false, className, ...props }: TableHeaderProps) {
	return (
		<thead
			className={cn('bg-navy-50 [&_tr]:border-b-2 [&_tr]:border-navy-200', sticky && 'sticky top-0 z-10', className)}
			{...props}
		/>
	)
}

// ─── Table Body / Footer ───────────────────────────────────────────────────

export function TableBody({ className, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) {
	return <tbody className={cn('[&_tr:last-child]:border-0', className)} {...props} />
}

export function TableFooter({ className, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) {
	return (
		<tfoot
			className={cn('border-t-2 border-navy-200 bg-cream/50 font-semibold [&_tr]:border-b-0', className)}
			{...props}
		/>
	)
}

// ─── Table Row ──────────────────────────────────────────────────────────────

export interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
	interactive?: boolean
	selected?: boolean
}

export function TableRow({ interactive = false, selected = false, className, ...props }: TableRowProps) {
	const { striped } = React.useContext(TableContext)

	return (
		<tr
			className={cn(
				'border-b border-border transition-colors duration-fast ease-default group',
				striped && 'even:bg-cream/30',
				interactive && 'hover:bg-cream/60 hover:shadow-sm cursor-pointer',
				selected && 'bg-gold/10 hover:bg-gold/15',
				interactive && 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/30 focus-visible:ring-inset',
				className,
			)}
			tabIndex={interactive ? 0 : undefined}
			{...props}
		/>
	)
}

// ─── Sort Icon ──────────────────────────────────────────────────────────────

export type SortDirection = 'asc' | 'desc' | null
export type SortIconVariant = 'chevrons' | 'caret' | 'text'

export function SortIcon({
	direction,
	variant = 'chevrons',
}: {
	direction: SortDirection
	variant?: SortIconVariant
}) {
	if (variant === 'text') {
		return (
			<span
				className={cn(
					'ml-1.5 inline-flex rounded-full px-1.5 py-0.5 text-[9px] font-ui font-semibold uppercase tracking-wider',
					direction ? 'bg-gold/15 text-gold' : 'bg-muted text-muted-foreground/70',
				)}
				aria-hidden="true"
			>
				{direction === 'asc' ? 'A–Z' : direction === 'desc' ? 'Z–A' : 'Sort'}
			</span>
		)
	}

	if (variant === 'caret') {
		return (
			<span className={cn('inline-flex ml-1 align-middle', direction ? 'text-gold' : 'text-muted-foreground/40')} aria-hidden="true">
				<svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor" className={cn(direction === 'desc' && 'rotate-180')}>
					<path d="M5 2L9 8H1Z" />
				</svg>
			</span>
		)
	}

	return (
		<span className={cn('inline-flex flex-col ml-1 leading-none', direction ? 'text-gold' : 'text-muted-foreground/40')} aria-hidden="true">
			<svg width="8" height="6" viewBox="0 0 8 6" fill="currentColor" className={cn(direction === 'asc' ? 'opacity-100' : 'opacity-30')}>
				<path d="M4 0L8 6H0Z" />
			</svg>
			<svg width="8" height="6" viewBox="0 0 8 6" fill="currentColor" className={cn('-mt-0.5', direction === 'desc' ? 'opacity-100' : 'opacity-30')}>
				<path d="M4 6L0 0H8Z" />
			</svg>
		</span>
	)
}

// ─── Table Head ─────────────────────────────────────────────────────────────

export interface TableHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
	align?: 'left' | 'center' | 'right'
	sortable?: boolean
	/** Optional sort direction for built-in header treatment */
	sortDirection?: SortDirection
	/** Sort indicator style */
	sortIconVariant?: SortIconVariant
}

export function TableHead({
	align = 'left',
	sortable = false,
	sortDirection = null,
	sortIconVariant = 'chevrons',
	className,
	children,
	...props
}: TableHeadProps) {
	const { compact } = React.useContext(TableContext)

	return (
		<th
			className={cn(
				'h-10 px-3 text-left font-semibold text-navy-700 uppercase tracking-wider',
				compact ? 'h-8 px-2 text-[10px]' : 'h-10 px-3 text-xs',
				align === 'center' && 'text-center',
				align === 'right' && 'text-right',
				sortable && 'cursor-pointer select-none hover:text-navy-900',
				sortDirection && 'text-navy-900',
				className,
			)}
			aria-sort={sortDirection === 'asc' ? 'ascending' : sortDirection === 'desc' ? 'descending' : sortable ? 'none' : undefined}
			{...props}
		>
			<span className={cn('inline-flex items-center', align === 'center' && 'justify-center', align === 'right' && 'justify-end w-full')}>
				{children}
				{sortable && <SortIcon direction={sortDirection} variant={sortIconVariant} />}
			</span>
		</th>
	)
}

// ─── Table Cell / Caption ───────────────────────────────────────────────────

export interface TableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
	align?: 'left' | 'center' | 'right'
}

export function TableCell({ align = 'left', className, ...props }: TableCellProps) {
	const { compact } = React.useContext(TableContext)
	return (
		<td
			className={cn('px-3 py-2.5 align-middle', compact && 'px-2 py-1.5', align === 'center' && 'text-center', align === 'right' && 'text-right', className)}
			{...props}
		/>
	)
}

export function TableCaption({ className, ...props }: React.HTMLAttributes<HTMLTableCaptionElement>) {
	return <caption className={cn('mt-4 text-sm text-muted-foreground font-body', className)} {...props} />
}

// ─── DataTable Hook ─────────────────────────────────────────────────────────

export type SortState = { key: string; dir: SortDirection }

export interface DataTableColumn<T = Record<string, unknown>> {
	key: string
	label: string
	sortable?: boolean
	align?: 'left' | 'center' | 'right'
	render?: (value: unknown, row: T) => React.ReactNode
	sortFn?: (a: T, b: T) => number
}

export interface UseDataTableOptions<T> {
	columns?: DataTableColumn<T>[]
	sort?: { key: string; dir: SortDirection }
	pageSize?: number
	filter?: (row: T, index: number) => boolean
}

export function useDataTable<T extends Record<string, unknown>>(data: T[], options: UseDataTableOptions<T> = {}) {
	const { columns = [], sort: initialSort, pageSize = 0, filter } = options
	const [sort, setSort] = React.useState<SortState>(initialSort ?? { key: '', dir: null })
	const [page, setPage] = React.useState(0)

	const columnMap = React.useMemo(() => new Map(columns.map(c => [c.key, c])), [columns])
	const headers = React.useMemo(() => columns.map(c => ({ key: c.key, label: c.label, sortable: c.sortable ?? false, align: c.align })), [columns])
	const filtered = React.useMemo(() => (filter ? data.filter(filter) : data), [data, filter])

	const sorted = React.useMemo(() => {
		if (!sort.key || !sort.dir) return filtered
		const col = columnMap.get(sort.key)
		if (col?.sortFn) {
			return [...filtered].sort((a, b) => (sort.dir === 'asc' ? col.sortFn!(a, b) : -col.sortFn!(a, b)))
		}
		return [...filtered].sort((a, b) => {
			const aVal = a[sort.key]
			const bVal = b[sort.key]
			if (aVal == null) return 1
			if (bVal == null) return -1
			const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true })
			return sort.dir === 'asc' ? cmp : -cmp
		})
	}, [filtered, sort, columnMap])

	const totalPages = pageSize > 0 ? Math.ceil(sorted.length / pageSize) : 1
	const paginated = pageSize > 0 ? sorted.slice(page * pageSize, (page + 1) * pageSize) : sorted

	React.useEffect(() => {
		if (page >= totalPages) setPage(Math.max(0, totalPages - 1))
	}, [totalPages, page])

	const toggleSort = (key: string) => {
		const col = columnMap.get(key)
		if (col && !col.sortable) return
		setSort(prev => {
			if (prev.key !== key) return { key, dir: 'asc' }
			if (prev.dir === 'asc') return { key, dir: 'desc' }
			return { key: '', dir: null }
		})
		setPage(0)
	}

	const getSortDirection = (key: string): SortDirection => (sort.key === key ? sort.dir : null)
	const getSortIcon = (key: string, variant: SortIconVariant = 'chevrons') => <SortIcon direction={getSortDirection(key)} variant={variant} />
	const goToPage = (p: number) => setPage(Math.max(0, Math.min(p, totalPages - 1)))

	return {
		headers,
		page: paginated,
		sorted,
		totalRows: filtered.length,
		unfilteredCount: data.length,
		pageIndex: page,
		totalPages,
		pageSize,
		sort,
		toggleSort,
		getSortDirection,
		getSortIcon,
		goToPage,
		nextPage: () => goToPage(page + 1),
		prevPage: () => goToPage(page - 1),
		hasNext: page < totalPages - 1,
		hasPrev: page > 0,
	}
}

// ─── DataTable Pagination ───────────────────────────────────────────────────

export interface DataTablePaginationProps {
	table: ReturnType<typeof useDataTable>
	label?: string
}

export function DataTablePagination({ table, label = 'rows', className, ...props }: DataTablePaginationProps & React.HTMLAttributes<HTMLDivElement>) {
	if (table.totalPages <= 1) return null

	const effectivePageSize = table.pageSize > 0 ? table.pageSize : table.totalRows || 1
	const start = table.pageIndex * effectivePageSize + 1
	const end = Math.min((table.pageIndex + 1) * effectivePageSize, table.totalRows)

	return (
		<div
			className={cn(
				'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between',
				'px-3 py-3 border-t border-border bg-cream/20',
				'text-xs text-muted-foreground font-ui',
				className,
			)}
			{...props}
		>
			<div className="flex items-center gap-2">
				<span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
					Page {table.pageIndex + 1} of {table.totalPages}
				</span>
				<span>
					Showing {start}–{end} of {table.totalRows} {label}
				</span>
			</div>

			<div className="flex items-center gap-1.5 self-end sm:self-auto">
				<button
					onClick={table.prevPage}
					disabled={!table.hasPrev}
					className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-cream/60 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-fast"
				>
					← Prev
				</button>
				<PagerButtons total={table.totalPages} current={table.pageIndex} onSelect={table.goToPage} />
				<button
					onClick={table.nextPage}
					disabled={!table.hasNext}
					className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-cream/60 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-fast"
				>
					Next →
				</button>
			</div>
		</div>
	)
}

function PagerButtons({ total, current, onSelect }: { total: number; current: number; onSelect: (p: number) => void }) {
	const pages: (number | '...')[] = []

	if (total <= 7) {
		for (let i = 0; i < total; i++) pages.push(i)
	} else {
		pages.push(0)
		if (current > 2) pages.push('...')
		for (let i = Math.max(1, current - 1); i <= Math.min(total - 2, current + 1); i++) pages.push(i)
		if (current < total - 3) pages.push('...')
		pages.push(total - 1)
	}

	return (
		<>
			{pages.map((p, i) =>
				p === '...' ? (
					<span key={`ellipsis-${i}`} className="px-1 text-muted-foreground">…</span>
				) : (
					<button
						key={p}
						onClick={() => onSelect(p)}
						className={cn(
							'inline-flex h-8 w-8 items-center justify-center rounded-md text-xs font-medium transition-colors duration-fast',
							current === p ? 'bg-navy text-cream-50 shadow-sm' : 'bg-background text-foreground hover:bg-cream/60 border border-border',
						)}
					>
						{p + 1}
					</button>
				),
			)}
		</>
	)
}
